import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import audioIcon from "../../assets/images/Audio.svg";
import WaveformPlayer from "./plater-wayform";
import { Controls } from "./play-lection-audio-controls";
import { useAudioPlayerContext } from "contexts/audio-context";
import useMakeScrollable from "custom_hooks/make-scrollable";
import WaveSurfer from "wavesurfer.js";
import { FINISH_SECTION } from "queries/mutations";

const PlayLectionAudio = ({ setHeaderTitle }) => {
  useMakeScrollable()
  const location = useLocation();
  const { name, audioUrl, id } = location.state || {};  
  const [isPlaying, setIsPlaying] = useState(false);
  const waveformRef = useRef(null);
  const waveSurferInstance = useRef(null);

  const {
    audioRef,
    setDuration,
    duration,
    progressBarRef,
    setTimeProgress
  } = useAudioPlayerContext();

  useEffect(() => {
    if (name) {
      setHeaderTitle(name);
    }
  }, [name, setHeaderTitle]);

  useEffect(() => {
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    if (audioRef.current) {
      audioRef.current.addEventListener("play", handlePlay);
      audioRef.current.addEventListener("pause", handlePause);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("play", handlePlay);
        audioRef.current.removeEventListener("pause", handlePause);
      }
    };
  }, []);

  const updateProgressBar = (currentTime) => {
    if (progressBarRef.current) {
      const progress = (currentTime / duration) * 100;
      progressBarRef.current.style.setProperty("--range-progress", `${progress}%`);
    }
  };

  useEffect(() => {
    if (waveformRef.current) {
      waveSurferInstance.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "#e0e0e0",
        progressColor: "#238CB9",
        barWidth: 2,
        barGap: 5,
        height: 100,
        cursorColor: "transparent",
      });

      if (audioRef.current) {
        waveSurferInstance.current.load(audioRef.current.src);
      }

      waveSurferInstance.current.on("ready", () => {
        if (audioRef.current) {
          setDuration(audioRef.current.duration);
        }
      });

      waveSurferInstance.current.on("audioprocess", () => {
        const currentTime = waveSurferInstance.current.getCurrentTime();
        setTimeProgress(currentTime);
        updateProgressBar(currentTime);
      });

      waveSurferInstance.current.on("seek", (progress) => {
        if (audioRef.current) {
          const newTime = progress * audioRef.current.duration;
          audioRef.current.currentTime = newTime;
          setTimeProgress(newTime);
          updateProgressBar(newTime);
        }
      });
    }

    return () => {
      waveSurferInstance.current?.destroy();
    };
  }, [audioRef, setDuration, setTimeProgress]);

  useEffect(() => {
    if (waveSurferInstance.current) {
      if (isPlaying) {
        waveSurferInstance.current.play();
      } else {
        waveSurferInstance.current.pause();
      }
    }
  }, [isPlaying]);

  const onLoadedMetadata = () => {
    const seconds = audioRef.current?.duration;
    if (seconds !== undefined) {
      setDuration(seconds);
      if (progressBarRef.current) {
        progressBarRef.current.max = seconds.toString();
      }
    }
  };

  const [finishSection] = useMutation(FINISH_SECTION, {
    onCompleted({ finishSection }) {
      if (finishSection.userHistory) {
        console.log('Lection was finished')
      } else {
        console.log('Failed to finish the lection')
      }
    },
  });

  const handleFinish = () => {
    const userId = localStorage.getItem("user_id");
    finishSection({
      variables: { userId: userId, sectionId: id },
    });
  };

  const togglePlay = () => {
    handleFinish()
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  return (
      <div className="lection-audio scrollable">
        <img src={audioIcon} className="lection-audio-image" alt="Audio Icon" />
        <div className="lection-audio-title">{name || "Audio Title"}</div>
        <audio ref={audioRef} src={audioUrl} onLoadedMetadata={onLoadedMetadata}/>
        <WaveformPlayer isPlaying={isPlaying} waveformRef={waveformRef} waveSurferInstance={waveSurferInstance}/>
        <Controls isPlaying={isPlaying} togglePlay={togglePlay} waveSurferInstance={waveSurferInstance} />
      </div>
  );
};

export default PlayLectionAudio;
